/**
 * Le relevé d'heures : ce que chaque compagnon a réellement fait dans sa journée.
 *
 * À ne pas confondre avec `chantier_pointages`, qui répartit des heures DÉJÀ
 * calculées entre les chantiers, pour le prix de revient. Ici on part de plus
 * bas : une heure d'arrivée, une heure de départ, une pause — ce que les
 * équipes écrivent le soir. Les heures à payer en sont déduites, elles ne sont
 * pas saisies.
 *
 * Deux principes tenus dans tout le fichier :
 *
 *  1. Une saisie illisible ne vaut pas zéro. Un horaire incohérent rend
 *     `null`, jamais 0 : une heure fausse à zéro se paie, et personne ne la
 *     voit passer.
 *  2. La règle de pause est explicite et visible. Elle n'est pas enfouie dans
 *     un calcul : elle est passée en paramètre, et chaque ligne dit ce qui lui
 *     a été retiré et pourquoi.
 *
 * Tout est en MINUTES, en nombres entiers. Les heures décimales (7,75 h) ne
 * servent qu'à l'affichage et à la paie, jamais au calcul : 8 h 45 + 7 h 30 en
 * décimal finit par dériver d'une minute, et une minute par jour sur six
 * salariés fait deux heures par an.
 */

/** Une heure de la journée, en minutes depuis minuit. 7 h 30 → 450. */
export type MinutesJour = number;

/** Une durée, en minutes. */
export type Minutes = number;

/** Un relevé, tel qu'il est stocké. */
export interface ReleveHeures {
  id: string;
  /** `AAAA-MM-JJ`. */
  jour: string;
  /** L'employé du CRM, quand le nom a pu être rattaché. */
  employeId: string | null;
  /** Le nom tel qu'il a été écrit ou saisi. Fait foi tant qu'il n'y a pas d'`employeId`. */
  nom: string;
  equipe: string | null;
  chantierId: string | null;
  chantierLibelle: string | null;
  debut: MinutesJour;
  fin: MinutesJour;
  /** Pause DÉCLARÉE. `null` = rien n'a été dit, ce qui n'est pas la même chose que 0. */
  pauseDeclaree: Minutes | null;
  note: string | null;
}

/**
 * Comment la pause est retirée.
 *
 * `respecterPauseDeclaree` à `false` — le réglage retenu par défaut — applique
 * `defautMinutes` partout, y compris les jours où l'équipe a écrit « sans
 * pause ». C'est un choix d'employeur, pas une donnée : le module le signale
 * chaque fois qu'il contredit ce qui a été déclaré.
 */
export interface ReglePause {
  /** Ce qui est retiré à une journée ordinaire. */
  defautMinutes: Minutes;
  /** `true` : la pause écrite fait foi. `false` : `defautMinutes` s'applique quand même. */
  respecterPauseDeclaree: boolean;
  /** En dessous de cette heure de fin, la journée est repérée comme courte. */
  finJourneeCourte: MinutesJour;
  /** `false` : rien n'est retiré aux journées courtes (pas de coupure déjeuner possible). */
  deduireSurJourneeCourte: boolean;
}

export const REGLE_PAUSE_DEFAUT: ReglePause = {
  defautMinutes: 60,
  respecterPauseDeclaree: false,
  finJourneeCourte: 14 * 60 + 30,
  deduireSurJourneeCourte: true,
};

export type MotifAlerte =
  /** Fin avant ou égale au début : la ligne ne peut pas être payée en l'état. */
  | 'horaire_incoherent'
  /** Aucune pause n'a été écrite ; c'est la règle qui a tranché. */
  | 'pause_non_declaree'
  /** Une pause a été écrite, la règle en a retenu une autre. */
  | 'pause_declaree_ignoree'
  /** Journée finissant tôt : y retirer une coupure déjeuner est discutable. */
  | 'journee_courte';

export interface LigneReleve {
  releve: ReleveHeures;
  /** Fin − début. `null` si l'horaire est incohérent. */
  brut: Minutes | null;
  /** Ce qui a été retiré, une fois la règle appliquée. */
  pauseAppliquee: Minutes;
  /** Ce qui est dû. `null` si l'horaire est incohérent — jamais 0 par défaut. */
  payable: Minutes | null;
  alertes: MotifAlerte[];
}

/** Applique la règle de pause à un relevé et dit ce qu'elle a fait. */
export function calculerLigne(releve: ReleveHeures, regle: ReglePause): LigneReleve {
  const alertes: MotifAlerte[] = [];

  if (
    !Number.isFinite(releve.debut) ||
    !Number.isFinite(releve.fin) ||
    releve.fin <= releve.debut
  ) {
    return {
      releve,
      brut: null,
      pauseAppliquee: 0,
      payable: null,
      alertes: ['horaire_incoherent'],
    };
  }

  const brut = releve.fin - releve.debut;
  const courte = releve.fin <= regle.finJourneeCourte;

  let pauseAppliquee: Minutes;
  if (releve.pauseDeclaree !== null && regle.respecterPauseDeclaree) {
    pauseAppliquee = Math.max(0, releve.pauseDeclaree);
  } else if (courte && !regle.deduireSurJourneeCourte) {
    pauseAppliquee = 0;
  } else {
    pauseAppliquee = Math.max(0, regle.defautMinutes);
  }

  if (releve.pauseDeclaree === null) {
    if (pauseAppliquee > 0) alertes.push('pause_non_declaree');
  } else if (releve.pauseDeclaree !== pauseAppliquee) {
    alertes.push('pause_declaree_ignoree');
  }
  if (courte && pauseAppliquee > 0) alertes.push('journee_courte');

  return { releve, brut, pauseAppliquee, payable: Math.max(0, brut - pauseAppliquee), alertes };
}

export interface RecapSalarie {
  employeId: string | null;
  nom: string;
  jours: number;
  brut: Minutes;
  pause: Minutes;
  payable: Minutes;
  /** Lignes dont l'horaire est incohérent : ni brut ni payable ne les comptent. */
  joursIllisibles: number;
  /** Nombre de lignes portant au moins une alerte, incohérences comprises. */
  joursAvecAlerte: number;
}

/**
 * Le récapitulatif par salarié, trié par nom.
 *
 * Le regroupement se fait sur l'`employeId` quand il existe, et sur le nom
 * sinon : deux relevés « Cyril » non rattachés restent ensemble, et ne se
 * mélangent pas au Cyril rattaché tant que quelqu'un ne les a pas reliés.
 */
export function recapParSalarie(
  releves: readonly ReleveHeures[],
  regle: ReglePause = REGLE_PAUSE_DEFAUT,
): RecapSalarie[] {
  const par = new Map<string, RecapSalarie>();

  for (const releve of releves) {
    const cle = releve.employeId ?? `nom:${releve.nom.trim().toLowerCase()}`;
    const r =
      par.get(cle) ??
      ({
        employeId: releve.employeId,
        nom: releve.nom,
        jours: 0,
        brut: 0,
        pause: 0,
        payable: 0,
        joursIllisibles: 0,
        joursAvecAlerte: 0,
      } satisfies RecapSalarie);

    const ligne = calculerLigne(releve, regle);
    if (ligne.brut === null || ligne.payable === null) {
      r.joursIllisibles += 1;
    } else {
      r.jours += 1;
      r.brut += ligne.brut;
      r.pause += ligne.pauseAppliquee;
      r.payable += ligne.payable;
    }
    if (ligne.alertes.length > 0) r.joursAvecAlerte += 1;

    par.set(cle, r);
  }

  return [...par.values()].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

export interface TotauxPeriode {
  jours: number;
  brut: Minutes;
  pause: Minutes;
  payable: Minutes;
  joursIllisibles: number;
}

export function totauxPeriode(recap: readonly RecapSalarie[]): TotauxPeriode {
  return recap.reduce<TotauxPeriode>(
    (t, r) => ({
      jours: t.jours + r.jours,
      brut: t.brut + r.brut,
      pause: t.pause + r.pause,
      payable: t.payable + r.payable,
      joursIllisibles: t.joursIllisibles + r.joursIllisibles,
    }),
    { jours: 0, brut: 0, pause: 0, payable: 0, joursIllisibles: 0 },
  );
}

/**
 * Les jours ouvrés d'une période où une équipe n'a rien déclaré.
 *
 * C'est le vrai service rendu : un oubli ne se voit pas dans un total, il se
 * voit dans un trou. Les équipes sont déduites des relevés eux-mêmes — une
 * équipe qui n'a jamais rien écrit de la période n'a pas de trou à signaler,
 * elle n'existe pas.
 */
export function joursSansReleve(
  releves: readonly ReleveHeures[],
  du: string,
  au: string,
  feries: readonly string[] = [],
): { equipe: string; jours: string[] }[] {
  const equipes = new Map<string, Set<string>>();
  for (const r of releves) {
    const eq = r.equipe ?? '—';
    const vus = equipes.get(eq) ?? new Set<string>();
    vus.add(r.jour);
    equipes.set(eq, vus);
  }

  const chomes = new Set(feries);
  const ouvres: string[] = [];
  for (const jour of joursDeLaPeriode(du, au)) {
    if (chomes.has(jour)) continue;
    const [y, m, d] = jour.split('-').map(Number);
    const jsem = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).getDay();
    if (jsem === 0 || jsem === 6) continue;
    ouvres.push(jour);
  }

  return [...equipes.entries()]
    .map(([equipe, vus]) => ({ equipe, jours: ouvres.filter((j) => !vus.has(j)) }))
    .filter((e) => e.jours.length > 0)
    .sort((a, b) => a.equipe.localeCompare(b.equipe, 'fr'));
}

/** Garde-fou : une période plus longue qu'un an est une erreur de saisie. */
const JOURS_PERIODE_MAX = 400;

function joursDeLaPeriode(du: string, au: string): string[] {
  const [ya, ma, da] = du.split('-').map(Number);
  const [yb, mb, db] = au.split('-').map(Number);
  if ([ya, ma, da, yb, mb, db].some((n) => n === undefined || Number.isNaN(n))) return [];

  const jours: string[] = [];
  const curseur = new Date(ya ?? 0, (ma ?? 1) - 1, da ?? 1);
  const fin = new Date(yb ?? 0, (mb ?? 1) - 1, db ?? 1);
  while (curseur <= fin && jours.length < JOURS_PERIODE_MAX) {
    jours.push(
      `${String(curseur.getFullYear())}-${pad(curseur.getMonth() + 1)}-${pad(curseur.getDate())}`,
    );
    curseur.setDate(curseur.getDate() + 1);
  }
  return jours;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ----- Lecture et écriture des heures ---------------------------------------

/** `450` → `« 7h30 »`. Une durée négative garde son signe. */
export function formatMinutes(minutes: Minutes): string {
  const signe = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  return `${signe}${String(Math.floor(abs / 60))}h${pad(abs % 60)}`;
}

/** `450` → `7.5`. Pour la paie, qui compte en heures décimales. */
export function enHeuresDecimales(minutes: Minutes): number {
  return Math.round((minutes / 60) * 100) / 100;
}

const HEURE_SEULE = /^(\d{1,2})$/;

/**
 * Lit une heure écrite à la main.
 *
 * Accepte ce que les équipes écrivent vraiment : `7h`, `7 h 30`, `7h30`,
 * `16 h15`, `15.30`, `14,30`, `7:30`. Rend `null` — jamais 0 — sur tout le
 * reste : une heure qu'on n'a pas su lire doit rester vide à l'écran, pas
 * devenir minuit.
 */
export function parseHeureJour(texte: string): MinutesJour | null {
  const t = texte.trim().toLowerCase().replace(/\s+/g, ' ');
  if (t === '') return null;

  let heures: number | null = null;
  let minutes = 0;

  const seule = HEURE_SEULE.exec(t);
  if (seule) {
    heures = Number(seule[1]);
  } else {
    const m = /^(\d{1,2})\s*[h:.,]\s*(\d{1,2})?$/.exec(t);
    if (!m) return null;
    heures = Number(m[1]);
    if (m[2] !== undefined && m[2] !== '') {
      // « 15.3 » n'est pas 15 h 3 : une minute s'écrit sur deux chiffres.
      if (m[2].length !== 2) return null;
      minutes = Number(m[2]);
    }
  }

  if (!Number.isInteger(heures) || heures < 0 || heures > 23) return null;
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) return null;
  return heures * 60 + minutes;
}

/**
 * Lit une pause écrite à la main.
 *
 * `« sans pause »` → 0. `« avec pause 30m »` → 30. `« avec pause »` sans durée
 * → `null` : on sait qu'elle a eu lieu, on ne sait pas combien de temps, et
 * inventer une durée serait pire que de laisser vide.
 *
 * Le texte peut être une phrase entière. La durée est alors cherchée AUTOUR du
 * mot « pause », et non dans toute la phrase : sur « 7h 15h45 avec pause 30m »,
 * chercher partout retiendrait le 7 h d'embauche comme durée de pause.
 */
export function parsePauseMinutes(texte: string): Minutes | null {
  const t = texte.trim().toLowerCase();
  if (t === '') return null;
  if (/\bsans\s+pause\b/.test(t)) return 0;

  const mot = t.lastIndexOf('pause');
  // Sans le mot « pause », le texte EST la durée : c'est le champ du formulaire.
  if (mot === -1) return dureeMinutes(t);
  // Avec lui, seule la durée qui SUIT compte. La chercher aussi avant ramasserait
  // l'heure de fin de journée sur « 7h 16h avec pause ».
  return dureeMinutes(t.slice(mot + 'pause'.length));
}

/** Une durée isolée : `30`, `30m`, `30 min`, `1h`, `1h30`, `0`. */
function dureeMinutes(texte: string): Minutes | null {
  const t = texte.trim();
  if (t === '') return null;

  const enHeures = /(\d{1,2})\s*h(?:\s*(\d{2}))?/.exec(t);
  if (enHeures) {
    const h = Number(enHeures[1]);
    const m = enHeures[2] === undefined ? 0 : Number(enHeures[2]);
    // Au-delà de huit heures ce n'est plus une pause, c'est une lecture ratée.
    if (h >= 0 && h <= 8 && m >= 0 && m <= 59) return h * 60 + m;
    return null;
  }

  const enMinutes = /(\d{1,3})/.exec(t);
  if (enMinutes) {
    const m = Number(enMinutes[1]);
    if (m >= 0 && m <= 480) return m;
  }
  return null;
}
